// frontend/src/pages/DashboardPage.jsx
import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import { getRuns, getRun, getTopics, getBrandVoices, triggerRun, getRunStats } from '../services/api';
import { Play, Clock, CheckCircle, XCircle, Loader, FileText, RefreshCw } from 'lucide-react';

const DashboardPage = () => {
  const { user } = useAuth();
  const [runs, setRuns] = useState([]);
  const [topics, setTopics] = useState([]);
  const [brandVoices, setBrandVoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sourceCounts, setSourceCounts] = useState({});
  const [showRunForm, setShowRunForm] = useState(false);
  const [runLoading, setRunLoading] = useState(false);
  const [runSuccess, setRunSuccess] = useState('');
  const [runError, setRunError] = useState('');
  const [activeRunId, setActiveRunId] = useState(null);
  const [stats, setStats] = useState({ total: 0, completed: 0, failed: 0, running: 0, month: '' });

  const [runForm, setRunForm] = useState({
    topic_ids: [],
    content_types: [],
    source_time_range: '24h',
    brand_voices: [],        // ← now an array (was single string 'brand_voice')
    additional_instructions: ''
  });

  useEffect(() => {
    loadData();
  }, []);

  // Poll for active run status
  useEffect(() => {
    if (!activeRunId) return;

    const interval = setInterval(async () => {
      try {
        const res = await getRun(activeRunId);
        const status = res.data.status;
        if (status === 'completed') {
          clearInterval(interval);
          setActiveRunId(null);
          setRunSuccess('Content generation complete! Check the Content Library.');
          loadData();
        } else if (status === 'failed') {
          clearInterval(interval);
          setActiveRunId(null);
          setRunError('Run failed: ' + (res.data.error_message || 'Unknown error'));
          loadData();
        }
      } catch (err) {
        console.error('Polling error', err);
      }
    }, 5000);

    return () => clearInterval(interval);
  }, [activeRunId]);

  const loadData = async () => {
    try {
      const [runsRes, topicsRes, voicesRes, statsRes] = await Promise.all([
        getRuns({ limit: 10 }),
        getTopics(),
        getBrandVoices(),
        getRunStats()
      ]);
      setRuns(runsRes.data);
      setTopics(topicsRes.data);
      setBrandVoices(voicesRes.data);
      setStats(statsRes.data);

      // Load source counts
      try {
        const countsRes = await api.get('/api/sources/counts');
        setSourceCounts(countsRes.data);
      } catch (e) {
        console.log('Could not load source counts');
      }
    } catch (err) {
      console.error('Failed to load dashboard data', err);
    } finally {
      setLoading(false);
    }
  };

  const handleTopicToggle = (topicId) => {
    setRunForm((prev) => ({
      ...prev,
      topic_ids: prev.topic_ids.includes(topicId)
        ? prev.topic_ids.filter((id) => id !== topicId)
        : [...prev.topic_ids, topicId]
    }));
  };

  const handleContentTypeToggle = (type) => {
    setRunForm((prev) => ({
      ...prev,
      content_types: prev.content_types.includes(type)
        ? prev.content_types.filter((t) => t !== type)
        : [...prev.content_types, type]
    }));
  };

  // ← New: toggle brand voice in/out of the array
  const handleBrandVoiceToggle = (voiceName) => {
    setRunForm((prev) => ({
      ...prev,
      brand_voices: prev.brand_voices.includes(voiceName)
        ? prev.brand_voices.filter((v) => v !== voiceName)
        : [...prev.brand_voices, voiceName]
    }));
  };

  const handleRunSubmit = async () => {
    if (runForm.topic_ids.length === 0) {
      setRunError('Please select at least one topic');
      return;
    }
    if (runForm.content_types.length === 0) {
      setRunError('Please select at least one content type');
      return;
    }
    if (runForm.brand_voices.length === 0) {
      setRunError('Please select at least one brand voice');
      return;
    }

    setRunLoading(true);
    setRunError('');
    setRunSuccess('');

    try {
      const res = await triggerRun(runForm);
      setActiveRunId(res.data.id);
      setRunSuccess('Run started — this page will update automatically when complete.');
      setShowRunForm(false);
      setRunForm({
        topic_ids: [],
        content_types: [],
        source_time_range: '24h',
        brand_voices: [],
        additional_instructions: ''
      });
      loadData();
    } catch (err) {
      setRunError(err.response?.data?.detail || 'Failed to start run');
    } finally {
      setRunLoading(false);
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed': return <CheckCircle size={16} className="text-green-500" />;
      case 'running': return <Loader size={16} className="text-blue-500 animate-spin" />;
      case 'failed': return <XCircle size={16} className="text-red-500" />;
      default: return <Clock size={16} className="text-yellow-500" />;
    }
  };

  const contentTypes = [
    { value: 'linkedin_post', label: 'LinkedIn Post' },
    { value: 'white_paper', label: 'White Paper' },
    { value: 'report', label: 'Report' },
    { value: 'article', label: 'Article' },
    { value: 'policy_brief', label: 'Policy Brief' }
  ];

  const timeRangeOptions = [
    { value: '1h', label: 'Past Hour' },
    { value: '24h', label: 'Last 24 Hours' },
    { value: '48h', label: 'Last 48 Hours' },
    { value: '7d', label: 'Last 7 Days' },
    { value: '14d', label: 'Last 2 Weeks' },
    { value: '30d', label: 'Last 30 Days' },
    { value: '90d', label: 'Last 3 Months' },
    { value: '1y', label: 'Last Year' },
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader className="animate-spin text-brand-mid" size={32} />
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl font-bold text-brand-dark">Dashboard</h1>
          <p className="text-gray-500 mt-1">Welcome back, {user?.name}</p>
        </div>
        <div className="flex gap-3">
          <button
            onClick={loadData}
            className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg cursor-pointer hover:bg-gray-50 transition-colors"
          >
            <RefreshCw size={18} />
            Refresh
          </button>
          <button
            onClick={() => setShowRunForm(!showRunForm)}
            className="flex items-center gap-2 px-6 py-2 bg-brand-dark text-white rounded-lg cursor-pointer hover:bg-brand-mid transition-colors"
          >
            <Play size={18} />
            Run Now
          </button>
        </div>
      </div>

      {/* Success/Error Messages */}
      {runSuccess && (
        <div className="bg-green-50 text-green-700 p-4 rounded-lg mb-6 flex items-center gap-2">
          <CheckCircle size={18} />
          {runSuccess}
        </div>
      )}
      {runError && (
        <div className="bg-red-50 text-red-600 p-4 rounded-lg mb-6 flex items-center gap-2">
          <XCircle size={18} />
          {runError}
        </div>
      )}

      {/* Run Now Form */}
      {showRunForm && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-8">
          <h2 className="text-lg font-semibold text-brand-dark mb-4">Configure Run</h2>

          {/* Topics */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">Topics</label>
            <div className="flex flex-wrap gap-3">
              {topics.filter(t => t.is_active).map((topic) => (
                <label key={topic.id} className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={runForm.topic_ids.includes(topic.id)}
                    onChange={() => handleTopicToggle(topic.id)}
                    className="rounded border-gray-300 text-brand-mid focus:ring-brand-mid cursor-pointer"
                  />
                  <span className="text-sm">{topic.name}</span>
                </label>
              ))}
            </div>
          </div>

          {/* Content Types */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">Content Types</label>
            <div className="flex flex-wrap gap-3">
              {contentTypes.map((type) => (
                <label key={type.value} className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={runForm.content_types.includes(type.value)}
                    onChange={() => handleContentTypeToggle(type.value)}
                    className="rounded border-gray-300 text-brand-mid focus:ring-brand-mid cursor-pointer"
                  />
                  <span className="text-sm">{type.label}</span>
                </label>
              ))}
            </div>
          </div>

          {/* Source Time Range */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">Source Time Range</label>
            <select
              value={runForm.source_time_range}
              onChange={(e) => setRunForm({ ...runForm, source_time_range: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-mid cursor-pointer"
            >
              {timeRangeOptions.map((opt) => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </div>

          {/* Brand Voice — now checkboxes */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Brand Voice
              <span className="ml-2 text-xs text-gray-400 font-normal">
                Select one or both — each generates a separate set of content
              </span>
            </label>
            <div className="flex flex-wrap gap-3">
              {brandVoices.filter(v => v.is_active !== 'false').map((voice) => (
                <label key={voice.id} className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={runForm.brand_voices.includes(voice.name)}
                    onChange={() => handleBrandVoiceToggle(voice.name)}
                    className="rounded border-gray-300 text-brand-mid focus:ring-brand-mid cursor-pointer"
                  />
                  <span className="text-sm">{voice.description || voice.name}</span>
                </label>
              ))}
            </div>
          </div>

          {/* Additional Instructions */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Additional Instructions (Optional)
            </label>
            <textarea
              value={runForm.additional_instructions}
              onChange={(e) => setRunForm({ ...runForm, additional_instructions: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-mid"
              rows={3}
              placeholder="Any specific guidance for this run..."
            />
          </div>

          {/* Submit */}
          <div className="flex justify-end gap-3">
            <button
              onClick={() => setShowRunForm(false)}
              className="px-4 py-2 border border-gray-300 rounded-lg cursor-pointer hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleRunSubmit}
              disabled={runLoading}
              className="flex items-center gap-2 px-6 py-2 bg-brand-dark text-white rounded-lg cursor-pointer hover:bg-brand-mid disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              <Play size={18} />
              {runLoading ? 'Starting...' : 'Start Run'}
            </button>
          </div>
        </div>
      )}

      {/* Quick Stats */}
      <div className="grid grid-cols-3 gap-6 mb-8">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <p className="text-sm text-gray-500">Runs This Month</p>
          <p className="text-3xl font-bold text-brand-dark mt-1">{stats.total}</p>
          <p className="text-xs text-gray-400 mt-1">{stats.month}</p>
        </div>
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <p className="text-sm text-gray-500">Active Topics</p>
          <p className="text-3xl font-bold text-brand-dark mt-1">{topics.filter(t => t.is_active).length}</p>
          {topics.filter(t => t.is_active).length > 0 && (
            <div className="mt-2 space-y-1">
              {topics.filter(t => t.is_active).map(t => (
                <div key={t.id} className="flex justify-between text-xs text-gray-500">
                  <span>{t.name}</span>
                  <span className="font-medium">{sourceCounts[t.id] || 0} sources</span>
                </div>
              ))}
            </div>
          )}
        </div>
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <p className="text-sm text-gray-500">Completed This Month</p>
          <p className="text-3xl font-bold text-brand-dark mt-1">{stats.completed}</p>
          <p className="text-xs text-gray-400 mt-1">{stats.month}</p>
        </div>
      </div>

      {/* Recent Runs */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200">
        <div className="p-6 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-brand-dark">Recent Runs</h2>
        </div>
        {runs.length === 0 ? (
          <div className="p-8 text-center text-gray-500">
            <FileText size={32} className="mx-auto mb-3 text-gray-300" />
            <p>No runs yet. Click "Run Now" to generate your first content.</p>
          </div>
        ) : (
          <div className="divide-y divide-gray-100">
            {runs.map((run) => (
              <div key={run.id} className="p-4 flex items-center justify-between hover:bg-gray-50 transition-colors">
                <div className="flex items-center gap-3">
                  {getStatusIcon(run.status)}
                  <div>
                    <p className="text-sm font-medium text-gray-700">
                      {run.trigger_type === 'automatic' ? 'Scheduled Run' : 'Manual Run'}
                    </p>
                    <p className="text-xs text-gray-500">
                      {new Date(run.created_at).toLocaleString()}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <span className={`text-xs px-2 py-1 rounded-full ${
                    run.status === 'completed' ? 'bg-green-100 text-green-700' :
                    run.status === 'running' ? 'bg-blue-100 text-blue-700' :
                    run.status === 'failed' ? 'bg-red-100 text-red-700' :
                    'bg-yellow-100 text-yellow-700'
                  }`}>
                    {run.status}
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default DashboardPage;